import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  catchError,
  debounceTime,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
} from 'rxjs';
import { GroupType } from '../../models/group.model';
import { Member } from '../../models/member.model';
import { SchoolMemberType } from '../../models/member.school.model';
import { MemberType, MembershipStatus } from '../../models/membership.status';
import { UserModel } from '../../models/user.model';
import { MemberService } from '../../services/member.service';
import { UserService } from '../../services/user.service';
import { UtilsService } from '../../services/utils.service';
import * as memberActions from '../actions/member.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class MemberEffects {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  memberService: MemberService = inject(MemberService);
  userService: UserService = inject(UserService);
  utilsService = inject(UtilsService);
  toastr: ToastrService = inject(ToastrService);
  private functions: AngularFireFunctions = inject(AngularFireFunctions);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);

  constructor(private location: Location) {}

  // effect to fetch use members from Firestore
  getMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.getMembers),
      mergeMap(({ groupId, memberType, lastRecord }) => {
        // console.log(
        //   `Fetching members: GroupId: ${groupId}, MemberType: ${memberType}, LastRecord: ${lastRecord}`
        // );
        let query: AngularFirestoreCollection<Member> =
          this.afs.collection<Member>('memberships', (ref) => {
            var reference = ref.where('groupId', '==', groupId);

            if (!memberType?.includes(MemberType.admin)) {
              reference = reference.where(
                'status',
                '==',
                MembershipStatus.active,
              );
            }

            reference = reference.orderBy('modified', 'desc');

            if (lastRecord) reference = reference.startAfter(lastRecord);

            reference = reference.limit(10);

            return reference;
          });
        return query.get();
      }),
      mergeMap((memberships) => {
        if (memberships.empty) return of([]);
        const members: Member[] = memberships.docs
          .map((doc) => {
            try {
              const member = doc.data() as Member;
              member.id = doc.id;
              // console.log(`Member: ${JSON.stringify(member)}`);
              return member;
            } catch (error) {
              return null;
            }
          })
          .filter((member) => member !== null); // Filter out null members
        return of(members);
      }),
      mergeMap((members) => {
        if (members.length === 0) return of(memberActions.getMembersDone());

        const finalMembers = members.map((member) => {
          return this.afs
            .doc<UserModel>('users/' + member.memberId)
            .get()
            .pipe(
              map((snapshot) => {
                if (!snapshot.exists) {
                  return member;
                }
                let memberDoc: Member = Object.assign(
                  {},
                  member,
                  snapshot.data(),
                );
                return memberDoc;
              }),
            );
        });

        return forkJoin(finalMembers).pipe(
          map((resolvedMembers) => {
            const validMembers = resolvedMembers.filter(
              (member) => member !== null,
            );
            // return of(memberActions.addMemberToStore({member: memberDoc}));
            return memberActions.addMembersToStore({ members: validMembers });
          }),
        );
      }),
      catchError((error) => {
        console.error('Error fetching members:', error);
        this.toastr.error(
          error?.message || 'An error occurred. Please try again later.',
          'Error Fetching Members',
        );
        return of(memberActions.getMembersFailure({ error }));
      }),
    ),
  );

  // Effect to create a new member
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.create),
      switchMap(async ({ member }) => {
        const _ = await this.memberService.create(member);
        const localMember: Member = {
          ...member,
          id: `${member.groupId}_${member.memberId}`,
          created: Timestamp.fromDate(moment(member.created).toDate()),
          modified: Timestamp.fromDate(moment(member.modified).toDate()),
        };
        this.toastr.success(
          'The member has been created successfully.',
          'Member Created',
        );
        this.location.back();
        return memberActions.createSuccess({ member: localMember });
      }),
      catchError((error) => {
        // console.error(`Error creating member: ${error}`);
        this.toastr.error(
          `An error occurred, '${error}'. Please try again later.`,
          'Error Creating Member',
        );
        return of(memberActions.createFailure({ error }));
      }),
    ),
  );

  // Effect to update a member
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.update),
      switchMap(async ({ id, member }) => {
        const _ = await this.memberService.update(id, member);
        const localMember: Member = {
          ...member,
          id: id,
          modified: Timestamp.fromDate(moment(member.modified).toDate()),
        };
        this.toastr.success(
          'The member details have been updated successfully.',
          'Member Updated',
        );
        this.location.back();
        return memberActions.updateSuccess({ member: localMember });
      }),
      catchError((error) => {
        // console.error(`Error updating member: ${error}`);
        this.toastr.error(
          `An error occurred, '${error}'. Please try again later.`,
          'Error Updating Member',
        );
        return of(memberActions.updateFailure({ error }));
      }),
    ),
  );

  // Effect to update a member status
  updateStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.updateStatus),
      switchMap(async ({ id, member }) => {
        const _ = await this.memberService.update(id, {
          ...member,
          modified: serverTimestamp() as Timestamp,
        });
        const localMember: Member = {
          ...member,
          id: id,
          modified: Timestamp.fromDate(moment(member.modified).toDate()),
        };
        return memberActions.updateStatusSuccess({ member: localMember });
      }),
      catchError((error) => {
        // console.error(`Error updating member: ${error}`);
        this.toastr.error(
          `An error occurred, '${error}'. Please try again later.`,
          'Error Updating Member Status',
        );
        return of(memberActions.updateStatusFailure({ error }));
      }),
    ),
  );

  // effect to delete a member
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.deleteAction),
      switchMap(({ id }) =>
        this.memberService
          .delete(id)
          .then(() => {
            this.toastr.success(
              'The member has been deleted successfully.',
              'Member Deleted',
            );
            // this.router.navigate(['/members']);
            return memberActions.deleteSuccess({ id: id });
          })
          .catch((error) => {
            // console.error(`Error deleting member: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Member',
            );
            return memberActions.deleteFailure({ error });
          }),
      ),
    ),
  );

  // Get New Members who joined in the last 30 days
  getMemberStatistics$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        memberActions.getMemberStats,
        memberActions.createSuccess,
        memberActions.updateStatusSuccess,
      ),
      mergeMap((action) => {
        let groupId = '';
        if (action.type === memberActions.getMemberStats.type) {
          groupId = action.groupId;
        } else if (action.type === memberActions.createSuccess.type) {
          groupId = action.member.groupId;
        } else if (action.type === memberActions.updateStatusSuccess.type) {
          groupId = action.member.groupId;
        }

        return from(this.memberService.getMemberStats(groupId)).pipe(
          switchMap((snapshot) => {
            let g: any = {
              id: groupId,
              newMembers: snapshot?.count ?? '-',
            };
            return of(memberActions.getMemberStatsDone({ group: g }));
          }),
          catchError((error) =>
            of(memberActions.getMemberStatsFailure({ error })),
          ),
        );
      }),
    ),
  );

  // Request membership to group
  // Check if the user is already a member of the group
  requestMembership$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.requestMembership),
      debounceTime(500),
      switchMap(async ({ group, uid }) => {
        const member: Member | null =
          await this.memberService.getMembershipById(group?.id, uid);
        if (member) {
          switch (member.status) {
            case MembershipStatus.active:
              this.toastr.error(
                'You are already a member of this group.',
                'Membership Exists',
              );
              break;
            case MembershipStatus.requested:
              this.toastr.error(
                'You have already requested membership to the group.',
                'Membership Request Exists',
              );
              break;
            default:
              this.toastr.error(
                `We found an existing membership in status ${member.status}`,
                'Membership Request Exists',
              );
              break;
          }
          this.router.navigate(['/groups']);
          return memberActions.requestMembershipFailure({
            error: 'Already a member',
          });
        }

        // console.log(
        //   `User not a member of the group. Requesting membership to group: ${groupId}`
        // );

        const user: UserModel | null = await this.userService.getUser(uid);

        if (user == null) {
          this.toastr.error(
            `Unable to get your user details. Please try again later.`,
            'Membership Request Failed',
          );
          return memberActions.requestMembershipFailure({
            error: 'User not found',
          });
        }

        // console.log(
        //   `Requesting membership for user: ${JSON.stringify(user, null, 2)}`
        // );

        const data: any = {
          name: user?.name ?? '',
          groupId: group?.id,
          memberId: uid,
          status: MembershipStatus.requested,
          memberType:
            group?.groupType == GroupType.school
              ? [SchoolMemberType.unassigned]
              : MemberType.member,
          memberColor: UtilsService.getRandomColor(),
          createdById: uid,
          created: serverTimestamp() as Timestamp,
          modified: serverTimestamp() as Timestamp,
        };

        await this.memberService.create(data);
        this.router.navigate(['/groups']);
        this.toastr.success(
          `Your membership request has been registered successfully. Please wait for the group admin to approve.`,
          'Membership Requested Successfully',
        );
        return memberActions.requestMembershipSuccess({ member: data });
      }),
      catchError((error) => {
        // console.error(`Error requesting membership: ${error}`);
        this.toastr.error(
          `An error occurred while registering your request. Please try again later.`,
          'Membership Request Failed',
        );
        return of(memberActions.requestMembershipFailure({ error }));
      }),
    ),
  );

  clearMembers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        return of(memberActions.clear());
      }),
    ),
  );
}
